import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { all, isNil, not, pathOr, pipe } from 'ramda'
import { useParams } from 'react-router-dom'

import { updateVailHistory } from '@/graphql/mutations'
import { graphQLRequest, queryClient, safeCypressID } from '@/utils'
import { AmplifyGraphQLclient } from '@/utils/amplify'
import {
  UpdateVailHistoryMutation,
  UpdateVailHistoryMutationVariables,
  VailHistory,
} from '@/utils/api'

import { validateAgainstWorkshop } from '../useValidate'

type HistoryTypes = 'engagementGoal' | 'solutions' | 'customComponent'

const fetchLimit = 500

const queries: Record<HistoryTypes, string> = {
  customComponent: /* GraphQL */ `
    query getComponentHistory($id: ID!, $nextToken: String) {
      getCustomComponent(id: $id) {
        history(limit: ${fetchLimit}, nextToken: $nextToken) {
          nextToken
          items {
            id
            name
            validConfig
            platformKeys {
              lowerEnv
              middleEnv
              upperEnv
              __typename
            }
            createdAt
            notes
            data
            createdBy {
              name
            }
            __typename
          }
          __typename
        }
        __typename
      }
    }
  `,

  engagementGoal: /* GraphQL */ `
    query getSolutionHistory($id: ID!, $nextToken: String) {
      getVailEngagementGoal(id: $id) {
        history(limit: ${fetchLimit}, nextToken: $nextToken) {
          nextToken
          items {
            id
            name
            validConfig
            createdAt
            platformKeys {
              lowerEnv
              middleEnv
              upperEnv
              __typename
            }
            notes
            data
            createdBy {
              name
            }
            __typename
          }
          __typename
        }
        __typename
      }
    }
  `,

  solutions: /* GraphQL */ `
    query getSolutionHistory($id: ID!, $nextToken: String) {
      getVailSolution(id: $id) {
        history(limit: ${fetchLimit}, nextToken: $nextToken) {
          nextToken
          items {
            id
            name
            validConfig
            platformKeys {
              lowerEnv
              middleEnv
              upperEnv
              __typename
            }
            createdAt
            notes
            data
            createdBy {
              name
            }
            __typename
          }
          __typename
        }
        __typename
      }
    }
  `,
}

export const useListHistory = <T extends unknown>(
  queryKey: (string | undefined | null | false)[],
  type: HistoryTypes,
  variables: { id?: string | null },
  validator?: (orgId: string, data: T) => Promise<object> | object
) => {
  const { orgID } = useParams<{ orgID: string }>()
  const { enqueueSnackbar } = useSnackbar()
  return useQuery({
    enabled: all(pipe(isNil, not), [...queryKey, orgID, variables.id]),
    queryFn: async () => {
      let histories: VailHistory[] = []
      let finishedGettingDocuments = false
      let nextToken: string | null | undefined
      do {
        const req = await AmplifyGraphQLclient.graphql({
          query: queries[type],
          variables: {
            nextToken,
            ...variables,
          },
        })

        const historyItem: Record<HistoryTypes, string> = {
          customComponent: 'getCustomComponent',
          engagementGoal: 'getVailEngagementGoal',
          solutions: 'getVailSolution',
        }

        histories = [
          ...histories,
          ...pathOr([], ['data', historyItem[type], 'history', 'items'], req),
        ] as VailHistory[]

        nextToken = pathOr(
          null,
          ['data', historyItem[type], 'history', 'nextToken'],
          req
        )
        if (!nextToken) finishedGettingDocuments = true
      } while (!finishedGettingDocuments)

      if (!!validator && !!orgID) {
        const filteredPromises = histories.filter((i) => i.validConfig === null)
        console.log('filteredPromises', filteredPromises)
        // we are going to run them when it wasn't run previously
        if (filteredPromises.length > 0)
          Promise.allSettled(
            filteredPromises.map(async (history) => {
              enqueueSnackbar('validating history items', {
                SnackbarProps: {
                  id: safeCypressID('validating history items'),
                },
                variant: 'info',
              })
              enqueueSnackbar('please wait for the validation', {
                SnackbarProps: {
                  id: safeCypressID('please wait for the validation'),
                },
                variant: 'info',
              })
              const data = JSON.parse(history.data)
              const isValid = await validateAgainstWorkshop(
                import.meta.env.VITE_APP_LOWER_ENV,
                await validator?.(orgID, data)
              )
              history.validConfig = !isValid.data.validateConfig?.error
              await graphQLRequest<
                UpdateVailHistoryMutation,
                UpdateVailHistoryMutationVariables
              >(updateVailHistory, {
                input: {
                  id: history.id,
                  validConfig: history.validConfig,
                },
              })
            })
          ).then(() => {
            queryClient.invalidateQueries({ queryKey })
          })
      }

      return histories.sort((a, b) => {
        return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      })
    },
    queryKey: queryKey,
  })
}
